import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { SearchForm } from "../components/search-form";
import { ResultsGrid } from "../components/results-grid";
import { checkHealth, searchByFile, searchByUrl } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { SearchResult, HealthResponse } from "../schema/schema";
import { MoonIcon, SunIcon, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { cn } from "../lib/utils";
import { useTheme } from "../hooks/use-theme";
import { queryClient } from "../lib/queryClient";

interface SearchHistoryItem {
  type: "file" | "url";
  value: string;
  timestamp: number;
}

interface SearchState {
  results: SearchResult;
  type: "file" | "url";
  value: string;
}

export default function Home() {
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null);
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  const [location, setLocation] = useLocation();

  // Load search history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem("searchHistory");
    if (savedHistory) {
      setSearchHistory(JSON.parse(savedHistory));
    }
  }, []);

  // Handle popstate event (browser back/forward)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const state = event.state as SearchState;
      if (state?.results) {
        setSearchResults(state.results);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const { data: health, isLoading: isHealthLoading, isError: isHealthError } = useQuery({
    queryKey: ["health"],
    queryFn: checkHealth,
  });

  // Adjust health check interval based on status
  useEffect(() => {
    if (health?.status === "Healthy") {
      queryClient.setQueryDefaults(["health"], { refetchInterval: 0 });
    } else {
      queryClient.setQueryDefaults(["health"], { refetchInterval: 5000 });
    }
  }, [health?.status]);

  const searchMutation = useMutation({
    mutationFn: async (data: {
      file?: File;
      url?: string;
      removeNsfw: boolean;
    }) => {
      let result;
      try {
        if (data.file) {
          result = await searchByFile(data.file, data.removeNsfw);
          addToHistory("file", data.file.name);
        } else if (data.url) {
          result = await searchByUrl(data.url, data.removeNsfw);
          addToHistory("url", data.url);
        } else {
          throw new Error("Either file or URL must be provided");
        }

        // Update browser history
        const searchState: SearchState = {
          results: result,
          type: data.file ? "file" : "url",
          value: data.file ? data.file.name : data.url!,
        };
        window.history.pushState(searchState, '', location);

        return result;
      } catch (error) {
        // If search fails and server was previously healthy, recheck health
        if (health?.status === "Healthy") {
          queryClient.invalidateQueries({ queryKey: ["health"] });
        }
        throw error;
      }
    },
    onSuccess: (data) => {
      setSearchResults(data);
    },
    onError: (error) => {
      toast({
        title: "Search failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const truncateUrl = (url: string, maxLength = 30) => {
    if (url.length <= maxLength) return url;
    const start = url.substring(0, maxLength / 2);
    const end = url.substring(url.length - maxLength / 2);
    return `${start}...${end}`;
  };

  const addToHistory = (type: "file" | "url", value: string) => {
    const newHistory = [
      { type, value, timestamp: Date.now() },
      ...searchHistory.slice(0, 9), // Keep last 10 searches
    ];
    setSearchHistory(newHistory);
    localStorage.setItem("searchHistory", JSON.stringify(newHistory));
  };

  const handleSearchImage = (url: string) => {
    searchMutation.mutate({ url, removeNsfw: true });
  };

  const handleHistoryClick = (item: SearchHistoryItem) => {
    if (item.type === "url") {
      searchMutation.mutate({ url: item.value, removeNsfw: true });
    }
  };

  const getServerStatus = () => {
    if (isHealthLoading) return { color: "bg-yellow-500", text: "Checking server health..." };
    if (isHealthError || !health) return { color: "bg-red-500", text: "Server is offline" };
    if (health?.status !== "Healthy") return { color: "bg-yellow-500", text: "Server is unhealthy" };
    return { color: "bg-green-500", text: "Server is online" };
  };

  const serverStatus = getServerStatus();
  const isServerDown = isHealthError || !health?.status || health?.status !== "Healthy";

  return (
    <div className="min-h-screen bg-background relative">
      {/* Dotted background pattern */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="w-full h-full dark:bg-[radial-gradient(circle,_#ffffff10_1px,_transparent_1px)] bg-[radial-gradient(circle,_#00000015_1px,_transparent_1px)] [background-size:24px_24px]" />
      </div>

      <div className="container mx-auto px-4 py-8 space-y-8 relative">
        <div className="flex justify-between items-center">
          <div className="flex gap-4">
            <a
              href="https://github.com/ordinall"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Made with ❤️ by <u>ordinall</u>
            </a>
            <div className="w-px h-5.8 bg-muted" />
            <a
              href="https://www.linkedin.com/posts/abdullahaansari_i-recently-built-an-ai-reverse-image-search-activity-7239335850025582592-MhWK"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            ><u>How was it made</u>
              
            </a>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          >
            {theme === "dark" ? (
              <SunIcon className="h-5 w-5" />
            ) : (
              <MoonIcon className="h-5 w-5" />
            )}
          </Button>
        </div>

        <div className="flex flex-col items-center justify-center pt-8 pb-2 space-y-6">
          <h1 className="text-4xl md:text-5xl font-bold text-center bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent pb-2 px-4">
            Wallpaper Engine Reverse Image Search
          </h1>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-1 text-center px-4">
            <div className="flex items-center gap-2">
              <div className={cn(
                "w-2 h-2 rounded-full animate-pulse",
                serverStatus.color
              )} />
              <span className={cn(
                "text-sm",
                isServerDown ? "text-destructive" : "text-muted-foreground"
              )}>
                {serverStatus.text}.
              </span>
            </div>
            {(
              <span className="text-sm text-muted-foreground sm:inline">
                413,619 searches performed · Database updated on September 8, 2024
              </span>
            )}
          </div>
            <div className="flex items-center gap-2 py-2">
              <Badge variant="secondary" className="bg-blue-500/10 text-blue-500 border-blue-500/20 hover:bg-blue-500/20 shrink-0">
                {"Update"}
              </Badge>
              <div 
                className="text-sm text-blue-500/90 dark:text-blue-400/90 transition-colors [&>a]:underline [&>a]:font-medium"
                dangerouslySetInnerHTML={{ __html: "The backend for this website will be shutting down on \n<span style=\"color: red; font-weight: bold;\">21 April 2026</span>.\n\nThis is due to me stepping away from my job to pursue a master’s degree, which means I’m no longer able to cover the server costs. I do wish to continue seeing this thing work and to update the dataset since its two years old now, but I would be unable to do that if I can not sustain it with donations. Thank you all for using this service :)" }}
              />
            </div>
        </div>

        <SearchForm
          onSearch={(data) => searchMutation.mutate(data)}
          isLoading={searchMutation.isPending}
          isDisabled={isServerDown}
          isPasteDisabled={isServerDown}
        />

        {searchHistory.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>Recent Searches</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {searchHistory.map((item, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => handleHistoryClick(item)}
                  disabled={item.type === "file" || isServerDown}
                  className="text-xs"
                >
                  {item.type === "file" ? `File: ${item.value}` : truncateUrl(item.value)}
                </Button>
              ))}
            </div>
          </div>
        )}

        {searchMutation.isPending && (
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
            <p className="mt-2 text-muted-foreground">Searching...</p>
          </div>
        )}

        {searchResults && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold">Search Results</h2>
              <p className="text-sm text-muted-foreground">
                Found in {searchResults.processing_time.toFixed(2)}s
              </p>
            </div>
            <ResultsGrid 
              results={searchResults} 
              onSearchImage={handleSearchImage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
